// =========================================================================
// AZ Gruppe — Animated Brand Pattern (single, reusable system)
//
// A thin "route / circuit" pattern that expresses the A → Z journey and the
// connection between AZ Gruppe's services. Crisp SVG vectors (no raster image),
// animated purely with CSS (traveling signal dashes + gentle node pulses), so
// there is no JS animation loop, no canvas and no new dependency.
//
// LAYERING CONTRACT (see also index.css .az-bp*):
//   - This layer is `position:absolute; inset:0; z-index:0; pointer-events:none`
//     and `aria-hidden`. It renders ABOVE a section's own light background but
//     BELOW the content, which must sit on `position:relative; z-index:10`.
//   - It never covers images/video/logos: those live in the content layer at a
//     higher z-index, and photographic sections simply don't mount this layer.
//
// The composition hugs the TOP and BOTTOM of each section as routed "rails",
// leaving the centre as clear negative space. Two compositions (a / b) plus
// per-variant colours keep neighbouring sections from looking tiled. Rails run
// slightly past the edges so no endpoint ever dangles in view, and the top/
// bottom anchoring means the pattern survives every aspect ratio (incl. tall
// mobile) without horizontal overflow.
// =========================================================================

// Colour sets. Values may be plain hex or a CSS variable reference (used by the
// `service` variant so insurance pages inherit green via --svc-primary, with no
// red, automatically). They are injected as CSS custom properties on the <svg>.
const VARIANTS = {
  corporate: { line: "#193D6D", accent: "#7A1516", node: "#C99A3D", opacity: 0.6 },
  automotive: { line: "#193D6D", accent: "#7A1516", node: "#C99A3D", opacity: 0.6 },
  insurance: { line: "#193D6D", accent: "#156F39", node: "#156F39", opacity: 0.62 },
  contact: { line: "#193D6D", accent: "#7A1516", node: "#7A1516", opacity: 0.58 },
  subtle: { line: "#193D6D", accent: "#193D6D", node: "#193D6D", opacity: 0.32 },
  hero: { line: "#6f8bb5", accent: "#C99A3D", node: "#C99A3D", opacity: 0.5 },
  // Service pages: inherit the page's own theme (navy line, primary accent —
  // red for Gutachten/Zulassung/Services, green for Versicherung).
  service: {
    line: "var(--svc-dark,#193D6D)",
    accent: "var(--svc-primary,#7A1516)",
    node: "var(--svc-primary,#7A1516)",
    opacity: 0.55,
  },
};

// Each composition is a set of routed rails (top + bottom) and their node dots.
// `signal` rails carry a slow traveling comet; `more` items are desktop-only so
// mobile stays lighter. Rails intentionally start/end past the 0–1440 viewBox.
const COMPOSITIONS = {
  a: {
    rails: [
      { d: "M-40 110 H360 Q392 110 392 142 V168 Q392 200 424 200 H648 Q680 200 680 168 V142 Q680 110 712 110 H1480", signal: true, dur: 27, delay: 0 },
      { d: "M-40 64 H840 Q872 64 872 96 V150 Q872 182 904 182 H1480", more: true },
      { d: "M-40 790 H300 Q332 790 332 758 V732 Q332 700 364 700 H720 Q752 700 752 732 V758 Q752 790 784 790 H1480", signal: true, dur: 33, delay: 6 },
      { d: "M-40 840 H620 Q652 840 652 808 V760 Q652 728 684 728 H1480", more: true },
    ],
    nodes: [
      { x: 392, y: 142, pulse: true, delay: 0 },
      { x: 680, y: 142, node: true },
      { x: 872, y: 96, more: true, pulse: true, delay: 2.4 },
      { x: 332, y: 732, node: true },
      { x: 752, y: 732, pulse: true, delay: 1.2 },
      { x: 652, y: 808, more: true },
      { x: 424, y: 200, accent: true, more: true },
    ],
  },
  b: {
    rails: [
      { d: "M-40 96 H520 Q552 96 552 128 V158 Q552 190 584 190 H980 Q1012 190 1012 158 V128 Q1012 96 1044 96 H1480", signal: true, dur: 30, delay: 3 },
      { d: "M-40 156 H300 Q332 156 332 124 V96 Q332 64 364 64 H1480", more: true },
      { d: "M-40 812 H440 Q472 812 472 780 V752 Q472 720 504 720 H860 Q892 720 892 752 V780 Q892 812 924 812 H1480", signal: true, dur: 35, delay: 8 },
      { d: "M-40 764 H680 Q712 764 712 796 V840 Q712 872 744 872 H1480", more: true },
    ],
    nodes: [
      { x: 552, y: 128, pulse: true, delay: 0.8 },
      { x: 1012, y: 128, node: true },
      { x: 332, y: 96, more: true },
      { x: 472, y: 780, node: true },
      { x: 892, y: 780, pulse: true, delay: 2 },
      { x: 712, y: 796, more: true, pulse: true, delay: 3.2 },
      { x: 584, y: 190, accent: true, more: true },
    ],
  },
};

/**
 * BrandPatternLayer — drop one inside any light (or dark, via `variant="hero"`)
 * section as the FIRST child, then keep the section's content wrapper on
 * `relative z-10`. Everything else (colours, animation, mobile reduction,
 * reduced-motion) is handled here + in index.css.
 *
 * @param {string} variant  corporate | automotive | insurance | contact | subtle | hero | service
 * @param {"a"|"b"} composition  pick alternating values on stacked sections
 * @param {string} className  extra classes (e.g. custom opacity/mask)
 */
export const BrandPatternLayer = ({
  variant = "corporate",
  composition = "a",
  className = "",
}) => {
  const v = VARIANTS[variant] || VARIANTS.corporate;
  const comp = COMPOSITIONS[composition] || COMPOSITIONS.a;

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className={`az-bp ${className}`}
      style={{
        opacity: v.opacity,
        "--bp-line": v.line,
        "--bp-accent": v.accent,
        "--bp-node": v.node,
      }}
      viewBox="0 0 1440 900"
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Base rails */}
      <g className="az-bp-field">
        {comp.rails.map((r, i) => (
          <path key={`l${i}`} d={r.d} className={`az-bp-line${r.more ? " az-bp-more" : ""}`} />
        ))}
      </g>

      {/* Traveling signal comets (accent colour) */}
      {comp.rails
        .filter((r) => r.signal)
        .map((r, i) => (
          <path
            key={`s${i}`}
            d={r.d}
            className="az-bp-signal"
            pathLength="100"
            style={{ animationDuration: `${r.dur}s`, animationDelay: `${r.delay}s` }}
          />
        ))}

      {/* Connection nodes */}
      <g className="az-bp-nodes">
        {comp.nodes.map((n, i) => (
          <circle
            key={`n${i}`}
            cx={n.x}
            cy={n.y}
            r={n.accent ? 4 : 3.3}
            className={[
              "az-bp-node",
              n.accent ? "is-accent" : "",
              n.pulse ? "is-pulse" : "",
              n.more ? "az-bp-more" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={n.pulse ? { animationDelay: `${n.delay || 0}s` } : undefined}
          />
        ))}
      </g>
    </svg>
  );
};
